import { useState, useRef, useCallback, useEffect } from 'react';
import axios from 'axios';
import { Wallpaper } from '../../components/Wallpaper';
import { ErrorPage } from '../ErrorPage';
import { isMobile } from '../../utils/utils';
import { QRCodeSVG } from 'qrcode.react';
import { CollectImageTitle } from '../PersonalDocument/styles';
import { Logo } from '../../components';
import { Footer } from '../../components/Footer';
import { Box } from '@mui/material';

export const FaceRecognitionBatch = (props) => {
    const {
        signBatchData,
        setSignerBatchStep,
        setSignBatchData,
        clientThemeSettings
    } = props;

    const [error, setError] = useState({ has_error: false, status: 0 });
    const [isSdkReady, setIsSdkReady] = useState(false);
    const [isTokenNeeded, setIsTokenNeeded] = useState(true);

    const sdkContainerRef = useRef(null);
    const instanceRef = useRef(null);

    const handleReturnDocumentDisplay = useCallback(() => {
        setSignerBatchStep('document_display');
    }, [setSignerBatchStep]);

    const getClientSessionKey = useCallback(async () => {
        if (!signBatchData.signer_token) {
            setError({ has_error: true, status: 401 });
            return null; 
        }
        try {
            const config = { headers: { Authorization: signBatchData.signer_token } };
            const response = await axios.get('signer_batch/liveness_client_session_key', config);
            const clientSessionKey = response.data.client_session_key;
            if (!clientSessionKey) throw new Error("Response does not contain 'client_session_key'.");
            return clientSessionKey;
        } catch (err) {
            const status = (err.response || {}).status;
            setError({ has_error: true, status: [401, 403, 409].includes(status) ? status : 500 });
            return null;
        }
    }, [signBatchData.signer_token]);

    const handleSuccess = useCallback((result) => {
        const imageKey = result.data;
        const payload = { event_date: new Date().toISOString(), image_key: imageKey, step: 'face_recognition' };
        const config = { headers: { Authorization: signBatchData.signer_token } };

        axios.patch('/signer_batch', payload, config)
            .then(response => {
                setSignBatchData({ ...signBatchData, ...response.data });
                setSignerBatchStep(response.data.next_step);
            })
            .catch(err => {
                const status = (err.response || {}).status;
                setError({ has_error: true, status: [401, 403, 409].includes(status) ? status : 500 });
            });
    }, [signBatchData, setSignBatchData, setSignerBatchStep]);

    const handleError = useCallback((err) => {
        if (err?.reason === 'INVALID_TOKEN') {
            setIsTokenNeeded(true);
        } else if (err?.reason === 'USER_CANCELED') {
            handleReturnDocumentDisplay();
        } else {
            setError({ has_error: true, status: 500 });
        }
    }, [handleReturnDocumentDisplay]);

    useEffect(() => {
        const hostComponent = sdkContainerRef.current;
        if (!hostComponent || !signBatchData?.session_id) {
            return;
        }

        const showBackButtonValue = false; 

        const webFaceRecon = new window.ZaigWebFaceRecon.WebFaceRecon(hostComponent)
            .setLogLevel('debug')
            .setSessionId(signBatchData.session_id)
            .setThemeConfiguration({
                buttonColor: clientThemeSettings['button_color'],
                fontColor: clientThemeSettings['font_color'],
                backgroundColor: clientThemeSettings['background_color'],
            })
            .setShowInvalidTokenScreen(true)
            .setShowBackButton(showBackButtonValue);
        
        if (process.env.REACT_APP_ENV !== 'production') {
            webFaceRecon.setSandboxEnvironment();
        }

        instanceRef.current = webFaceRecon.build();
        instanceRef.current.initialize()
            .then(() => {
                setIsSdkReady(true); 
            })
            .catch(initErr => {
                setError({ has_error: true, status: 500 });
            })

        return () => {
            if (hostComponent) {
                hostComponent.innerHTML = '';
            }
        };
    }, [clientThemeSettings, signBatchData.session_id]); 

    useEffect(() => {
        if (!isSdkReady || !instanceRef.current || !isTokenNeeded) {
            return;
        }

        const executeSdkOpen = async () => {
            const token = await getClientSessionKey();
            if (token) {
                instanceRef.current.open(token)
                    .then(handleSuccess)
                    .catch(handleError);
            }
        };

        executeSdkOpen();
    }, [isSdkReady, getClientSessionKey, handleSuccess, handleError, isTokenNeeded]);

    const qrCodeUrl = signBatchData.signer_batch_data?.birthdate
        ? `${window.location.href}?document_number=${signBatchData.signer_batch_data.document_number}&birthdate=${signBatchData.signer_batch_data.birthdate?.split('-').reverse().join('-')}`
        : `${window.location.href}?document_number=${signBatchData.signer_batch_data.document_number}`;


   if (!isMobile()) {
        return (
            <Wallpaper backgroundColor={clientThemeSettings['background_color']} style={{ padding: '10px' }}>
                <div className="base-container base-container-width base-container-padding" style={{ backgroundColor: 'rgb(245, 245, 242)' }}>
                    <Logo imageSource={clientThemeSettings.logo_url} />
                    <Box>
                        <CollectImageTitle>
                            Agora precisamos realizar a prova de vida. Escaneie o QR Code abaixo para continuar a jornada pelo seu dispositivo móvel.
                        </CollectImageTitle>
                        <div style={{ display: 'flex' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-evenly', width: '100%', height: '100%' }}>
                                <QRCodeSVG value={qrCodeUrl} width={'70%'} height={'70%'} />
                            </div>
                        </div>
                    </Box>
                    <Footer />
                </div>
            </Wallpaper>
        );
    }
  
    return (
    <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw', 
        height: '100vh', 
        zIndex: 1000   
    }}>
        <Wallpaper id="faceReconContainer" backgroundColor={clientThemeSettings['background_color']}>
            <div ref={sdkContainerRef} style={{ width: '100%', height: '100%' }} />
            
            {error.has_error && (
                <ErrorPage
                    error={error}
                    redirectToLogin={() => setSignerBatchStep('login')}
                    resetError={() => setError({ has_error: false, status: 0 })}
                    clientThemeSettings={clientThemeSettings}
                />
            )}
        </Wallpaper>
    </div>
);
};